import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Target,
  CheckCircle,
  XCircle,
  SkipForward,
  Loader2,
  Eye,
  AlertCircle,
  FileDown,
  BookOpen,
} from 'lucide-react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useStudentAttempts } from '../hooks/useStudentAttempts'
import { assessmentService } from '@/features/test-series/services/testSeriesService'
import type { TestAttemptDetailResponse, TestAttemptStatus } from '../types'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'
import { trackAnalyticsViewed } from '@/shared/utils/analytics'

// Format date for display
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

// Format time for display
const formatTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Format duration from milliseconds
const formatDuration = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

// Get status badge styling
const getStatusBadge = (status: TestAttemptStatus) => {
  const styles: Record<TestAttemptStatus, { bg: string; text: string; label: string }> = {
    SUBMITTED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
    IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In Progress' },
    ABANDONED: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Abandoned' },
    TIMED_OUT: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Timed Out' },
  }
  return styles[status]
}

// Get report download URL
const getReportUrl = (attemptId: string): string => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
  return `${baseUrl}/mnb-test-reports/mock_test_feedback_${attemptId}.pdf`
}

// Mobile Card Component for each attempt
function AttemptCard({
  attempt,
  testName,
  onViewResults,
  onReviewQuestions,
}: {
  attempt: TestAttemptDetailResponse
  testName: string
  onViewResults: () => void
  onReviewQuestions: () => void
}) {
  const statusBadge = getStatusBadge(attempt.status)
  const canViewResults = attempt.status === 'SUBMITTED' || attempt.status === 'TIMED_OUT'

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
      {/* Header: Test name and status */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">{testName}</p>
          <p className="text-xs text-gray-500">
            Attempt #{attempt.attemptNumber} • {formatDate(attempt.startedAt)} at {formatTime(attempt.startedAt)}
          </p>
        </div>
        <span
          className={`flex-shrink-0 inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusBadge.bg} ${statusBadge.text}`}
        >
          {statusBadge.label}
        </span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-lg font-semibold text-gray-900">
            {attempt.totalMarksObtained.toFixed(0)}
          </p>
          <p className="text-xs text-gray-500">Score</p>
        </div>
        <div className="bg-green-50 rounded-lg p-2">
          <p className="text-lg font-semibold text-green-600">{attempt.totalCorrect}</p>
          <p className="text-xs text-gray-500">Correct</p>
        </div>
        <div className="bg-red-50 rounded-lg p-2">
          <p className="text-lg font-semibold text-red-600">{attempt.totalIncorrect}</p>
          <p className="text-xs text-gray-500">Wrong</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-lg font-semibold text-gray-600">{attempt.totalSkipped}</p>
          <p className="text-xs text-gray-500">Skipped</p>
        </div>
      </div>

      {/* Footer: Duration, Accuracy, Action */}
      <div className="pt-2 border-t border-gray-100 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-gray-600">
              <Clock className="w-4 h-4" />
              {formatDuration(attempt.totalTimeMs)}
            </span>
            <span
              className={`font-medium ${
                attempt.accuracy >= 70
                  ? 'text-green-600'
                  : attempt.accuracy >= 50
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }`}
            >
              {attempt.accuracy.toFixed(1)}% accuracy
            </span>
          </div>
        </div>
        {canViewResults && (
          <div className="flex items-center gap-2">
            <a
              href={getReportUrl(attempt.attemptId)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
              title="Download PDF Report (may take 2-3 min to generate)"
            >
              <FileDown className="w-4 h-4" />
              Report
            </a>
            <button
              onClick={onViewResults}
              className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
            >
              <Eye className="w-4 h-4" />
              View
            </button>
            <button
              onClick={onReviewQuestions}
              className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Review
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default function MyPerformance() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [page, setPage] = useState(0)
  const [statusFilter, setStatusFilter] = useState<TestAttemptStatus | undefined>(undefined)
  const [testNames, setTestNames] = useState<Record<string, string>>({})
  const pageSize = 10
  const analyticsTrackedRef = useRef(false)

  const studentEmail = user?.email || user?.phoneNumber || ''

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('analytics', 'my_performance', 'My Performance')
  }, [])

  // Track analytics_viewed for GA4
  useEffect(() => {
    if (!analyticsTrackedRef.current) {
      analyticsTrackedRef.current = true
      trackAnalyticsViewed('my_performance')
    }
  }, [])

  const { data, isLoading, isError, error } = useStudentAttempts({
    studentId: studentEmail,
    status: statusFilter,
    page,
    size: pageSize,
  })

  // Fetch test names for displayed attempts
  useEffect(() => {
    if (!data?.content.length) return

    const fetchTestNames = async () => {
      // Get unique test codes that we don't already have names for
      const uniqueCodes = [...new Set(data.content.map((a) => a.testCode))]
      const missingCodes = uniqueCodes.filter((code) => !testNames[code])

      if (missingCodes.length === 0) return

      // Fetch all missing test names in parallel
      const results = await Promise.allSettled(
        missingCodes.map((code) => assessmentService.getAssessmentByCode(code))
      )

      const newNames: Record<string, string> = {}
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.data?.testName) {
          newNames[missingCodes[index]] = result.value.data.testName
        }
      })

      if (Object.keys(newNames).length > 0) {
        setTestNames((prev) => ({ ...prev, ...newNames }))
      }
    }

    fetchTestNames()
  }, [data?.content])

  const handleViewResults = (attempt: TestAttemptDetailResponse) => {
    navigate(`/mock-test/${attempt.testCode}/results/${attempt.attemptId}`)
  }

  const handleReviewQuestions = (attempt: TestAttemptDetailResponse) => {
    navigate(`/mock-test/${attempt.testCode}/review/${attempt.attemptId}`)
  }

  const handlePreviousPage = () => {
    if (page > 0) setPage(page - 1)
  }

  const handleNextPage = () => {
    if (data?.page.hasNext) setPage(page + 1)
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">My Performance</h1>
        <p className="text-sm sm:text-base text-gray-600">
          Track your test attempts and analyze your performance
        </p>
      </div>

      {/* Report Generation Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <FileDown className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-900">PDF Reports Available</p>
          <p className="text-sm text-blue-700">
            Detailed AI-generated PDF reports are available for completed tests. Reports may take 2-3 minutes to generate after test completion.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        {/* Filter Bar */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Test Attempts</h2>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter || ''}
              onChange={(e) => {
                setStatusFilter(e.target.value as TestAttemptStatus || undefined)
                setPage(0)
              }}
              className="flex-1 sm:flex-none px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="SUBMITTED">Completed</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="TIMED_OUT">Timed Out</option>
              <option value="ABANDONED">Abandoned</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="p-12 flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-4" />
            <p className="text-gray-600">Loading your test attempts...</p>
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="p-12 flex flex-col items-center justify-center">
            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            <p className="text-gray-900 font-medium mb-2">Failed to load attempts</p>
            <p className="text-gray-600 text-sm">
              {error?.message || 'An error occurred while fetching your test attempts.'}
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !isError && data?.content.length === 0 && (
          <div className="p-12 flex flex-col items-center justify-center">
            <Target className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-gray-900 font-medium mb-2">No test attempts yet</p>
            <p className="text-gray-600 text-sm text-center">
              Start taking tests to build your performance history.
              <br />
              Your attempts will appear here.
            </p>
          </div>
        )}

        {/* Content */}
        {!isLoading && !isError && data && data.content.length > 0 && (
          <>
            {/* Mobile: Card Layout */}
            <div className="md:hidden p-4 space-y-3">
              {data.content.map((attempt) => (
                <AttemptCard
                  key={attempt.attemptId}
                  attempt={attempt}
                  testName={testNames[attempt.testCode] || attempt.testCode}
                  onViewResults={() => handleViewResults(attempt)}
                  onReviewQuestions={() => handleReviewQuestions(attempt)}
                />
              ))}
            </div>

            {/* Desktop: Table Layout */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Test
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-600" />
                        Correct
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <span className="inline-flex items-center gap-1">
                        <XCircle className="w-3 h-3 text-red-600" />
                        Wrong
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <span className="inline-flex items-center gap-1">
                        <SkipForward className="w-3 h-3 text-gray-500" />
                        Skipped
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3 text-blue-600" />
                        Duration
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Accuracy
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.content.map((attempt) => {
                    const statusBadge = getStatusBadge(attempt.status)
                    return (
                      <tr
                        key={attempt.attemptId}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {testNames[attempt.testCode] || attempt.testCode}
                            </p>
                            <p className="text-xs text-gray-500">
                              Attempt #{attempt.attemptNumber}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <p className="text-sm text-gray-900">{formatDate(attempt.startedAt)}</p>
                            <p className="text-xs text-gray-500">{formatTime(attempt.startedAt)}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusBadge.bg} ${statusBadge.text}`}
                          >
                            {statusBadge.label}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="font-semibold text-gray-900">
                            {attempt.totalMarksObtained.toFixed(1)}
                          </span>
                          <span className="text-gray-500 text-sm">
                            /{attempt.totalQuestions * 4}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-green-600 font-medium">{attempt.totalCorrect}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-red-600 font-medium">{attempt.totalIncorrect}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-gray-600">{attempt.totalSkipped}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-gray-900">{formatDuration(attempt.totalTimeMs)}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span
                            className={`font-medium ${
                              attempt.accuracy >= 70
                                ? 'text-green-600'
                                : attempt.accuracy >= 50
                                  ? 'text-yellow-600'
                                  : 'text-red-600'
                            }`}
                          >
                            {attempt.accuracy.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          {attempt.status === 'SUBMITTED' || attempt.status === 'TIMED_OUT' ? (
                            <div className="flex items-center justify-center gap-2">
                              <a
                                href={getReportUrl(attempt.attemptId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-colors"
                                title="Download PDF Report (may take 2-3 min to generate)"
                              >
                                <FileDown className="w-4 h-4" />
                                Report
                              </a>
                              <button
                                onClick={() => handleViewResults(attempt)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                                View
                              </button>
                              <button
                                onClick={() => handleReviewQuestions(attempt)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-md transition-colors"
                              >
                                <BookOpen className="w-4 h-4" />
                                Review
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-gray-600 text-center sm:text-left">
                Showing {page * pageSize + 1} to{' '}
                {Math.min((page + 1) * pageSize, data.page.totalElements)} of{' '}
                {data.page.totalElements} attempts
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePreviousPage}
                  disabled={data.page.first}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Prev
                </button>
                <span className="px-3 py-1.5 text-sm text-gray-600">
                  {data.page.number + 1} / {data.page.totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={data.page.last}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
